import React from 'react'
import classnames from 'classnames/bind'
import * as blueprint from '@adc/blueprint'
import { Text, useApplicationContext } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import type { Maybe } from '@adc/sdk/types'
import { internationalization } from '@adc/constants'
import {
  formatMoneyWithNumeral,
  formatMoneyWithCentsExceptZero,
} from 'common/utils/formatters'
import { useListingContext } from 'common/context/listing'
import {
  ReserveTooltip,
  BidDepositTooltip,
} from 'common/components/bidBanner/tooltips'
import styles from './styles.scss'
import { isPaymentTypeFclOnline } from 'common/utils/listing'
const cx = classnames.bind(styles)

interface LabelValueProps {
  label: React.ReactNode
  value: string | number
  valueColor?: string
  dataElmId?: string
}

// TODO: Remove duplication, replace this with LabelValue component in design-system
const LabelValue = ({
  label,
  value,
  valueColor,
  dataElmId,
}: LabelValueProps) => {
  return (
    <div className={cx('bid-detail')} data-elm-id={dataElmId}>
      {typeof label === 'string' ? (
        <Text
          className={cx('u-mb-1')}
          content={label}
          data-elm-id={dataElmId ? `${dataElmId}-label` : 'label'}
          color='gray'
        />
      ) : (
        label
      )}
      <Text
        content={value}
        data-elm-id={dataElmId ? `${dataElmId}-value` : 'value'}
        weight='heavy'
        color={valueColor || 'gray'}
      />
    </div>
  )
}

interface OpeningBidProps {
  openingBid: number | null | undefined
}

const OpeningBid = ({ openingBid }: OpeningBidProps) => {
  const value = formatMoneyWithCentsExceptZero(openingBid, '-') as string
  return value ? (
    <LabelValue
      label='Opening Bid'
      value={value}
      dataElmId='opening_bid'
      data-elm-id='opening_bid'
    />
  ) : null
}

interface CurrentBidProps {
  currentBidAmount: Maybe<number> | undefined
}

const CurrentBid = ({ currentBidAmount }: CurrentBidProps) => {
  const value = formatMoneyWithCentsExceptZero(currentBidAmount, '-') as string
  return (
    <LabelValue
      label='Current Bid'
      value={value}
      dataElmId='current_bid'
      data-elm-id='current_bid'
    />
  )
}

const OpeningOrCurrentBid = () => {
  const { segment, listing } = useListingContext()
  if (segment?.bid_count) {
    return (
      <CurrentBid currentBidAmount={segment.current_highest_bid?.bid_amount} />
    )
  } else if (!listing?.listing_summary?.show_opening_bid) {
    return <OpeningBid openingBid={null} />
  } else {
    return <OpeningBid openingBid={segment?.starting_bid_amount} />
  }
}

interface BidIncrementProps {
  bidIncrement: number | null | undefined
}

const BidIncrement = ({ bidIncrement }: BidIncrementProps) => {
  const value = formatMoneyWithNumeral(bidIncrement, '-') as string
  return (
    <LabelValue
      label='Bid Increment'
      value={value}
      dataElmId='bid_increment'
      data-elm-id='bid_increment'
    />
  )
}

const Reserve = () => {
  const { segment } = useListingContext()
  let reserveLabelValue = ''
  switch (segment.reserve_status) {
    case graph.enums.ReserveStatus.ReserveMet:
      reserveLabelValue = 'Met'
      break
    case graph.enums.ReserveStatus.ReserveNotMet:
    case graph.enums.ReserveStatus.SubjectToReserveMet:
      reserveLabelValue = 'Not Met'
      break
    case graph.enums.ReserveStatus.ReserveNotSet:
      reserveLabelValue = 'Not Set'
      break
  }

  const hasReserveBeenMet =
    segment.reserve_status === graph.enums.ReserveStatus.ReserveMet
  return (
    <LabelValue
      label={
        <div className={cx('u-mb-1')}>
          <Text
            as='span'
            className={cx('u-mr-1')}
            content='Reserve'
            data-elm-id='reserve-label'
            color='gray'
          />
          <ReserveTooltip />
        </div>
      }
      dataElmId='reserve'
      data-elm-id='reserve'
      value={reserveLabelValue}
      valueColor={hasReserveBeenMet ? 'success' : 'caution'}
    />
  )
}

const EstCreditBid = () => {
  const { segment, listing } = useListingContext()
  const { featureFlags } = useApplicationContext()
  if (
    featureFlags?.['fe.resi.enable-credit-bid-reveal'] &&
    listing.listing_status === graph.enums.ListingStatus.Canceled
  )
    return null
  let value = ''
  let valueColor = ''
  switch (segment.reserve_status) {
    case graph.enums.ReserveStatus.ReserveMet:
      value = 'Exceeded'
      valueColor = 'success'
      break
    case graph.enums.ReserveStatus.ReserveNotMet:
      value = 'Not Exceeded'
      valueColor = 'caution'
      break
    case graph.enums.ReserveStatus.ReserveNotSet:
      return null
  }
  return (
    <LabelValue
      label='Est. Credit Bid'
      dataElmId='est_credit_bid'
      data-elm-id='est_credit_bid'
      value={value}
      valueColor={valueColor}
    />
  )
}

interface BidDepositProps {
  bidDeposit: number | null | undefined
}

const BidDeposit = ({ bidDeposit }: BidDepositProps) => {
  if (!bidDeposit) return null
  const value = blueprint.utils.formatMoney(bidDeposit, {}, true)
  return (
    <LabelValue
      label={
        <div className={cx('u-mb-1')}>
          <Text
            as='span'
            className={cx('u-mr-1')}
            content='Deposit'
            data-elm-id='label'
            color='gray'
          />
          <BidDepositTooltip />
        </div>
      }
      value={value}
      dataElmId='bid_deposit'
      data-elm-id='bid_deposit'
      valueColor='success'
    />
  )
}

const AutoBid = () => {
  const { segment } = useListingContext()
  // update this to be null if record status is inactive, can use the segment function hasValidMaxBid
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const maxBidAmount = segment.__methods__?.hasValidMaxBid?.()
    ? segment?.current_user_max_bid?.max_bid_amount
    : undefined
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const value = formatMoneyWithCentsExceptZero(maxBidAmount, '-') as string
  return maxBidAmount ? (
    <LabelValue
      label={internationalization.your_max_bid_label}
      value={value}
      dataElmId='max_bid'
      data-elm-id='max_bid'
    />
  ) : null
}

interface BidDetailsSectionProps {
  containerClassName?: string
  bidDeposit?: number | null | undefined
}

export const Before = ({
  containerClassName,
  bidDeposit,
}: BidDetailsSectionProps) => {
  return (
    <div className={cx('container', containerClassName)}>
      <AutoBid />
      <OpeningOrCurrentBid />
      <LabelValue
        label='Bid Increment'
        value='N/A'
        dataElmId='bid_increment'
        data-elm-id='bid_increment'
      />
      <BidDeposit bidDeposit={bidDeposit} />
    </div>
  )
}

export const During = ({
  containerClassName,
  bidDeposit,
}: BidDetailsSectionProps) => {
  const { segment, listing } = useListingContext()
  const { featureFlags } = useApplicationContext()

  const depositAmount = isPaymentTypeFclOnline(listing, featureFlags)
    ? listing.current_user_bid_deposit_requirements?.absolute_amount_required
    : bidDeposit

  return (
    <div className={cx('container', containerClassName)}>
      <AutoBid />
      <OpeningOrCurrentBid />
      <BidIncrement bidIncrement={segment.current_increment_amount} />
      {!listing.__methods__?.isTrustee() ? <Reserve /> : <EstCreditBid />}
      <BidDeposit bidDeposit={depositAmount} />
    </div>
  )
}

export const After = ({
  containerClassName,
  bidDeposit,
}: BidDetailsSectionProps) => {
  const { listing } = useListingContext()
  return (
    <div className={cx('container', containerClassName)}>
      {!listing.__methods__?.isTrustee() ? <Reserve /> : <EstCreditBid />}
      {bidDeposit && <BidDeposit bidDeposit={bidDeposit} />}
    </div>
  )
}
